import { useState } from 'react';
import { ArrowLeft, AlertTriangle, Camera, ChevronRight, X, TreePine, Leaf, Sparkles, MapPin, Sprout } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FORAGING, FORAGING_WARNING } from '../lib/foraging';
import { useI18n } from '../i18n';
import ShareButton from '../components/ShareButton';
import LogFindModal from '../components/LogFindModal';
import Tilt from '../components/living/Tilt';
import SpeciesIcon from '../components/species/SpeciesIcon';
import SpeciesHero from '../components/species/SpeciesHero';
import MakeRecipeButton from '../components/MakeRecipeButton';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function Foraging() {
    const { locale, t } = useI18n();
    const navigate = useNavigate();
    const [selected, setSelected] = useState(null);
    const [tab, setTab] = useState('trees');
    const [logFind, setLogFind] = useState(null);

    const tr = (obj) => (obj && (obj[locale] || obj.en || obj.da)) || '';
    const filtered = FORAGING.filter((p) => p.category === tab);
    const currentMonth = new Date().getMonth() + 1;
    const inSeason = (p) => Array.isArray(p.season) && p.season.includes(currentMonth);

    return (
        <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto" data-testid="foraging-page">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> {t('common.back')}</button>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">{t('wild.eyebrow')}</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 mb-5">{t('wild.title')}</h1>
            <button type="button" data-testid="forage-camera" onClick={() => navigate('/id?then=log&src=guide')} className="w-full mb-4 inline-flex justify-center items-center gap-2 bg-brand-green text-white py-3 rounded-full text-sm"><Camera size={16} />{locale === 'da' ? 'Identificér et fund med kameraet' : 'Identify a find with the camera'}</button>

            {/* Advisory disclaimer — always visible */}
            <div className="mb-6 flex items-start gap-2 text-xs text-brand-terracotta bg-brand-terracotta/10 border border-brand-terracotta/30 rounded-2xl p-3" data-testid="wild-advisory">
                <AlertTriangle size={14} strokeWidth={1.8} className="flex-shrink-0 mt-0.5" />
                <span>{tr(FORAGING_WARNING)}</span>
            </div>

            <div className="flex gap-2 mb-6">
                <button
                    data-testid="tab-trees"
                    onClick={() => setTab('trees')}
                    className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-sm transition-colors ${tab === 'trees' ? 'bg-brand-green text-white' : 'bg-brand-card border border-brand-border text-brand-ink hover:bg-brand-bg2'}`}
                >
                    <TreePine size={14} strokeWidth={1.8} /> {t('wild.tab_trees')}
                </button>
                <button
                    data-testid="tab-herbs"
                    onClick={() => setTab('herbs')}
                    className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-sm transition-colors ${tab === 'herbs' ? 'bg-brand-green text-white' : 'bg-brand-card border border-brand-border text-brand-ink hover:bg-brand-bg2'}`}
                >
                    <Leaf size={14} strokeWidth={1.8} /> {t('wild.tab_herbs')}
                </button>
            </div>

            <div className="space-y-3 stagger">
                {filtered.map((p) => (
                    <Tilt
                        as="button"
                        key={p.id}
                        data-testid={`forage-${p.id}`}
                        onClick={() => setSelected(p)}
                        max={5}
                        className="block w-full text-left bg-brand-card border border-brand-border rounded-3xl"
                    >
                        <div className="flex items-center p-4 gap-4">
                            <SpeciesIcon kind="forage" id={p.id} emoji={p.emoji} name={tr(p.name)} size={60} className="depth-2" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="font-serif text-lg text-brand-ink truncate">{tr(p.name)}</div>
                                    {inSeason(p) && (
                                        <span data-testid={`badge-season-${p.id}`} className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-semibold bg-brand-green/15 text-brand-green px-2 py-0.5 rounded-full">
                                            <Sparkles size={9} /> {t('wild.in_season')}
                                        </span>
                                    )}
                                    {p.region === 'na' && (
                                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-semibold bg-brand-ochre/20 text-brand-ochre px-2 py-0.5 rounded-full">
                                            <MapPin size={9} /> {t('wild.north_america')}
                                        </span>
                                    )}
                                    {p.caution && (
                                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-semibold bg-brand-terracotta/15 text-brand-terracotta px-2 py-0.5 rounded-full">
                                            <AlertTriangle size={9} /> {t('wild.care')}
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-brand-ink3 mt-0.5 truncate">{tr(p.edible_parts)}</div>
                            </div>
                            <ChevronRight size={16} className="text-brand-ink3 flex-shrink-0" />
                        </div>
                    </Tilt>
                ))}
            </div>

            {selected && (
                <div className="fixed inset-0 z-[60] bg-brand-ink/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setSelected(null)}>
                    <div className="bg-brand-bg w-full sm:max-w-2xl rounded-t-3xl sm:rounded-3xl border border-brand-border max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="forage-detail">
                        <SpeciesHero kind="forage" id={selected.id} emoji={selected.emoji} name={tr(selected.name)} className="aspect-[16/9] rounded-t-3xl" />
                        <div className="p-6">
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <SpeciesIcon kind="forage" id={selected.id} emoji={selected.emoji} name={tr(selected.name)} size={56} photo={false} />
                                    <div>
                                        <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-green">
                                            {selected.category === 'trees' ? t('wild.tab_trees') : t('wild.tab_herbs')}
                                        </span>
                                        <h2 className="font-serif text-2xl sm:text-3xl text-brand-ink leading-tight">{tr(selected.name)}</h2>
                                    </div>
                                </div>
                                <button data-testid="forage-detail-close" onClick={() => setSelected(null)} className="p-2 hover:bg-brand-bg2 rounded-full -mr-2 -mt-2"><X size={18} /></button>
                            </div>

                            <MakeRecipeButton source={`catalog:wild:${selected.id}`} className="mt-3" />
                            <div className="space-y-1 mt-4">
                                <p className="text-sm text-brand-ink3"><strong className="text-brand-ink2">{t('wild.edible_parts')}:</strong> {tr(selected.edible_parts)}</p>
                                <p className="text-sm text-brand-ink3"><strong className="text-brand-ink2">{t('wild.habitat')}:</strong> {tr(selected.habitat)}</p>
                                <p className="text-sm text-brand-ink3"><strong className="text-brand-ink2">{t('wild.season')}:</strong> {selected.season.map((m) => MONTHS[m - 1]).join(', ')}</p>
                            </div>

                            <div className="mt-4 bg-brand-green/5 border border-brand-green/20 rounded-2xl p-3" data-testid="forage-id-features">
                                <div className="text-xs uppercase tracking-widest text-brand-green font-semibold mb-1">{t('wild.how_to_id')}</div>
                                <p className="text-sm text-brand-ink2 leading-relaxed">{tr(selected.id_features)}</p>
                            </div>

                            <div className="mt-3 bg-brand-ochre/10 border border-brand-ochre/30 rounded-2xl p-3" data-testid="forage-uses">
                                <div className="text-xs uppercase tracking-widest text-brand-ochre font-semibold mb-1">{t('wild.uses')}</div>
                                <p className="text-sm text-brand-ink2 leading-relaxed">{tr(selected.uses)}</p>
                            </div>

                            {selected.lookalike_warning && (
                                <div className="mt-3 bg-brand-terracotta/10 border border-brand-terracotta/30 rounded-2xl p-3" data-testid="forage-warning">
                                    <div className="text-xs uppercase tracking-widest text-brand-terracotta font-semibold mb-1 inline-flex items-center gap-1"><AlertTriangle size={11} /> {t('wild.lookalike')}</div>
                                    <p className="text-sm text-brand-ink2 leading-relaxed">{tr(selected.lookalike_warning)}</p>
                                </div>
                            )}

                            <div className="mt-4 flex items-center justify-between">
                                <ShareButton
                                    title={`${tr(selected.name)} · GrowDaily Wild Food Guide`}
                                    text={`🌿 ${tr(selected.name)} — discover this edible wild plant in GrowDaily's Wild Food Guide!`}
                                    className="text-brand-green hover:text-brand-greenDark"
                                    label={t('common.share')}
                                />
                                <button
                                    data-testid="forage-log-find"
                                    onClick={() => setLogFind({ id: selected.id, name: tr(selected.name) })}
                                    className="inline-flex items-center gap-1.5 text-sm font-medium bg-brand-green text-white px-4 py-2 rounded-full hover:bg-brand-greenDark transition-colors"
                                >
                                    <Sprout size={15} strokeWidth={1.8} /> {t('wild.log_find')}
                                </button>
                            </div>

                            <p className="text-[10px] text-brand-ink3 mt-4 leading-relaxed">{tr(FORAGING_WARNING)}</p>
                        </div>
                    </div>
                </div>
            )}

            <LogFindModal
                open={!!logFind}
                onClose={() => setLogFind(null)}
                defaultKind="wild"
                defaultSpecies={logFind}
                autoLocate
                onLogged={() => navigate('/finds-map')}
            />
        </div>
    );
}
